/**
 * The demo's mobile "both sides" experience (SKE-4166, phase 2).
 *
 * Explicitly NOT the desktop split-screen at a smaller size. A phone has room
 * for one thing at a time, so the approver queue keeps the full width and the
 * marketer's side arrives in two deliberately different ways:
 *
 *   1. AUTOMATIC — after every action, a compact confirmation card slides up
 *      for a few seconds showing that post flipping to its new status on the
 *      team's side, then gets out of the way. This is the "killer moment"; it
 *      costs the visitor nothing and interrupts nothing.
 *   2. MANUAL — a persistent role toggle ("Client" / "Your team") opens the
 *      full mirror in a sheet, for a visitor who wants to inspect it properly.
 *
 * The toggle is the single source of truth for which side is on screen and sits
 * above the sheet, so it always reads the current state and always works.
 *
 * Under `prefers-reduced-motion: reduce` every slide is dropped by Tailwind's
 * `motion-safe:` gate and the reveal becomes an instant state change; the
 * scroll-into-view below switches to `auto` for the same reason.
 */

import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import { useEffect, useState } from 'react'

import { trackDemoTeaserViewed } from './demoAnalytics'
import type { MirrorNotice, MirrorPost } from './demoMirror'
import {
  DEMO_REVEAL_FADE,
  DEMO_REVEAL_RISE,
  demoRevealStyle,
} from './demoReveal'
import { demoStrings } from './demoStrings'
import MarketerMirror, { STATUS_DOT_CLASS, StatusChip } from './MarketerMirror'
import { useDemoMirror } from './useDemoMirror'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/** How long the automatic confirmation card stays up before the queue returns. */
const AUTO_REVEAL_MS = 3200

/** Roughly the sheet's slide duration — long enough for the row to exist. */
const SHEET_SETTLE_MS = 320

type DemoRole = 'client' | 'team'

/* -------------------------------------------------------------------------- */
/* Automatic confirmation card                                                 */
/* -------------------------------------------------------------------------- */

const FlipCard: React.FC<{
  notice: MirrorNotice
  post: MirrorPost | undefined
}> = ({ notice, post }) => (
  <div
    role="status"
    className={cn(
      'fixed inset-x-3 bottom-20 z-[55] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl',
      'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-300'
    )}
  >
    <div className="flex items-center gap-2 bg-blue-800 px-3 py-1.5 text-[11px] font-medium text-blue-100">
      <span
        aria-hidden="true"
        className="flex h-4 w-4 items-center justify-center rounded bg-blue-500 font-gotham text-[10px] font-extrabold text-white"
      >
        S
      </span>
      {demoStrings.mirrorPanelTitle}
    </div>

    <div className="flex items-start gap-2 px-3 py-2.5">
      <span
        aria-hidden="true"
        className={cn(
          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
          STATUS_DOT_CLASS[notice.status]
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-neutral-800">
          {notice.message}
        </p>
        {post ? (
          <div className="mt-2 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300">
            <StatusChip status={post.status} label={post.statusLabel} />
          </div>
        ) : null}
      </div>
    </div>
  </div>
)

/* -------------------------------------------------------------------------- */
/* Role toggle                                                                 */
/* -------------------------------------------------------------------------- */

const RoleToggle: React.FC<{
  role: DemoRole
  onChange: (role: DemoRole) => void
}> = ({ role, onChange }) => {
  // The active accent is side-driven — warm (spice) for the client's link,
  // blurple for your own workspace — matching the desktop frames' hairlines.
  const activeClass: Record<DemoRole, string> = {
    client: 'bg-spice-500 text-white',
    team: 'bg-blue-500 text-white',
  }

  const option = (value: DemoRole, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={role === value}
      className={cn(
        'rounded-full px-3 py-1.5 text-xs font-medium motion-safe:transition-colors',
        role === value
          ? activeClass[value]
          : 'text-neutral-600 hover:text-neutral-900'
      )}
    >
      {label}
    </button>
  )

  return (
    <div
      role="group"
      aria-label={demoStrings.roleToggleAriaLabel}
      // Step 2 of the load reveal — the last beat, and the one that points at
      // the demo's headline moment.
      style={demoRevealStyle(2)}
      className={cn(
        'fixed bottom-4 left-4 z-[70] flex items-center gap-0.5 rounded-full border border-neutral-200 bg-white/95 p-0.5 shadow-lg backdrop-blur',
        DEMO_REVEAL_RISE
      )}
    >
      {option('client', demoStrings.sideClientMobileLabel)}
      {option('team', demoStrings.sideTeamMobileLabel)}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Shell                                                                       */
/* -------------------------------------------------------------------------- */

const DemoMobileMirror: React.FC<React.PropsWithChildren> = ({ children }) => {
  const mirror = useDemoMirror()
  const prefersReducedMotion = usePrefersReducedMotion()

  const [role, setRole] = useState<DemoRole>('client')
  const [flash, setFlash] = useState<MirrorNotice | null>(null)

  const { latestNotice } = mirror

  // Auto-reveal after each action. `latestNotice` only gets a new identity when
  // the visitor actually does something, so this fires once per action and not
  // on unrelated re-renders.
  useEffect(() => {
    if (!latestNotice || role === 'team') {
      // Already looking at the team's side — a card on top of it would be noise.
      return
    }

    setFlash(latestNotice)
    const timer = setTimeout(() => setFlash(null), AUTO_REVEAL_MS)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestNotice])

  // Bring the post that just changed into view inside the sheet — with nine
  // posts the flipped one is often below the fold.
  useEffect(() => {
    if (role !== 'team' || !latestNotice) {
      return
    }

    const timer = setTimeout(
      () => {
        const row = document.querySelector(
          `[data-mirror-postid="${latestNotice.postId}"]`
        )
        row?.scrollIntoView?.({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        })
      },
      prefersReducedMotion ? 0 : SHEET_SETTLE_MS
    )

    return () => clearTimeout(timer)
  }, [role, latestNotice, prefersReducedMotion])

  const handleRoleChange = (next: DemoRole) => {
    setRole(next)
    // The sheet supersedes the transient card in both directions.
    setFlash(null)
  }

  const isSheetOpen = role === 'team'
  const flashedPost = flash
    ? mirror.posts.find((post) => post.id === flash.postId)
    : undefined

  // The "both sides at once" beat on a phone: either the automatic card or the
  // sheet the visitor opened. Once per visit — the guard lives in
  // `trackDemoTeaserViewed`, shared with the desktop shell.
  const isTeaserVisible = isSheetOpen || flash !== null
  useEffect(() => {
    if (!isTeaserVisible) {
      return
    }
    trackDemoTeaserViewed({
      surface: isSheetOpen ? 'mobile-sheet' : 'mobile-card',
      trigger: isSheetOpen ? 'manual' : 'auto',
    })
  }, [isTeaserVisible, isSheetOpen])

  return (
    <>
      {/* Step 0 of the load reveal, matching the desktop shell. A fade with no
          transform — see the containing-block note in demoReveal.ts. */}
      <div style={demoRevealStyle(0)} className={DEMO_REVEAL_FADE}>
        {children}
      </div>

      {flash && !isSheetOpen && <FlipCard notice={flash} post={flashedPost} />}

      <div
        data-testid="demo-team-sheet"
        // No label of its own: the MarketerMirror `<section>` inside already
        // names this region, and repeating it would announce the panel twice.
        // `aria-hidden` while closed keeps the off-screen sheet out of the
        // accessibility tree and out of the tab order's way.
        aria-hidden={!isSheetOpen}
        className={cn(
          'fixed inset-x-0 bottom-0 z-[60] h-[82vh] overflow-hidden rounded-t-2xl border-t border-neutral-200 bg-neutral-25 shadow-2xl',
          'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out',
          isSheetOpen ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        <MarketerMirror
          {...mirror}
          // The transient card already carried the message; inside the sheet the
          // rows themselves are the story, so don't stack toasts over them.
          notices={[]}
          className="pb-16"
          headerAction={
            <button
              type="button"
              onClick={() => handleRoleChange('client')}
              aria-label={demoStrings.roleClientLabel}
              className="rounded-full p-1 text-neutral-400 hover:bg-neutral-50 hover:text-neutral-700 motion-safe:transition-colors"
            >
              <i aria-hidden="true" className="fa-solid fa-xmark" />
            </button>
          }
        />
      </div>

      <RoleToggle role={role} onChange={handleRoleChange} />
    </>
  )
}

export default DemoMobileMirror
